import { useQuery } from 'react-query'
import axios from '../../utils/http/axios-local'
import { keysToCamel } from '../../utils/case-coverters'
import { DatasetDTO, DisputeDTO, BuyerIdentityDTO, RenderingDTO } from './dtos'
import { useEffect, useMemo } from 'react'

export const useDispute = (disputeId: string) => {
  const {
    data: disputeData,
    isLoading: isDisputeLoading,
    refetch: refetchDispute
  } = useQuery(
    `dispute-${disputeId}`,
    () => axios.get<DisputeDTO>(`/det/disputes/${disputeId}`),
    {
      refetchOnWindowFocus: false,
      enabled: !!disputeId
    }
  )

  return {
    disputeData: (disputeData?.data ?? null) as DisputeDTO,
    isDisputeLoading,
    refetchDispute
  }
}

export const useBuyerIdentity = (disputeId: string) => {
  const {
    data: response,
    isLoading,
    refetch,
    error
  } = useQuery({
    queryKey: ['buyer_identity', disputeId],
    queryFn: () => axios.get(`/api/disputes/${disputeId}/buyer_identity`),
    refetchOnWindowFocus: false,
    enabled: !!disputeId,
    retry: false
  })

  const transformedData = useMemo(() => {
    if (!response?.data) {
      return undefined
    }

    return keysToCamel(response.data)
  }, [response?.data])

  const isForbidden = (error as any)?.response?.status === 403
  const isAllowed = !isForbidden

  return {
    data: transformedData as BuyerIdentityDTO,
    isLoading,
    refetch,
    error: isForbidden ? null : error,
    isAllowed
  }
}

export const useRenderings = (disputeId: string) => {
  const {
    data: renderingsData,
    isLoading: isRenderingsLoading,
    refetch: refetchRenderings
  } = useQuery(
    `renderings-${disputeId}`,
    () => axios.get<RenderingDTO[]>(`/det/renderings?dispute_id=${disputeId}`),
    {
      refetchOnWindowFocus: false,
      enabled: !!disputeId
    }
  )

  return {
    renderingsData: (renderingsData?.data ?? {}) as RenderingDTO[],
    isRenderingsLoading,
    refetchRenderings
  }
}

export const useDataset = (disputeId: string) => {
  const {
    data: datasetData,
    isLoading: isDatasetLoading,
    refetch: refetchDataset
  } = useQuery(
    `dataset-${disputeId}`,
    () => axios.get<{ dataset: DatasetDTO }>(`/det/datasets/${disputeId}`),
    {
      refetchOnWindowFocus: false,
      enabled: !!disputeId
    }
  )

  return {
    datasetData: (datasetData?.data ?? {}) as DatasetDTO,
    isDatasetLoading,
    refetchDataset
  }
}

export const useDetData = (
  disputeId: string,
  manuallySubmittedStatus: boolean,
  dispute: DisputeDTO
) => {
  const { disputeData, isDisputeLoading, refetchDispute } =
    useDispute(disputeId)
  const { renderingsData, isRenderingsLoading, refetchRenderings } =
    useRenderings(disputeId)
  const { datasetData, isDatasetLoading, refetchDataset } =
    useDataset(disputeId)

  const isLoading = isDisputeLoading || isRenderingsLoading || isDatasetLoading

  useEffect(() => {
    refetchRenderings()
    refetchDataset()
  }, [manuallySubmittedStatus, dispute]) // re-fetch everything on dispute change

  return {
    isDETLoading: isLoading,
    dispute: disputeData,
    renderings: renderingsData ?? [],
    dataset: datasetData.dataset
  }
}
